import { Toast } from '@fattureincloud/fic-design-system'
import React, { FC, useEffect, useState } from 'react'

import { httpRequest, ResponseInterface } from '../../common/network'
import { errorMessage, locale } from '../../locales/locale'
import { useAppSelector } from '../../redux/hooks'
import {
  ArticleInfo,
  FixedSidebarItem,
  RelatedArticle,
  SidebarType,
  StatisticsArticle,
} from '../definitions'
import { AdminDataContainer, ArticleContent, ToolbarContainer } from './article.components'
import ArticleLateralActions from './ArticleLateralActions'
import FroalaEditor from './FroalaEditor'

interface Props {
  article: ArticleInfo
  statistics: StatisticsArticle
  hasSidebar: boolean
  setArticle: (value: ArticleInfo) => void
  changeView: (permalink: string, type: string) => void
  subArticle: boolean
  isExternal: boolean
  viewportWidth: number
  updateSidebar: (newSidebar: SidebarType) => void
  fixedSidebar: FixedSidebarItem
  embedded: boolean
  articlePreview: () => JSX.Element
}

const ArticleAdminMode: FC<Props> = (props: Props) => {
  const realm = useAppSelector((state) => state.realm.value)
  const [editedArticle, setEditedArticle] = useState<ArticleInfo>(props.article)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const editorModeActive = useAppSelector((state) => state.editor.value)

  useEffect(() => {
    setEditedArticle(props.article)
  }, [props.article])

  const deleteArticle = () => {
    setIsDeleting(true)
    httpRequest({
      method: 'DELETE',
      path: `help/article/${editedArticle?.id}`,
      data: {
        realm: realm,
      },
      onSuccess: () => {
        setIsDeleting(false)
        props.setArticle(editedArticle)
        props.changeView(editedArticle.parent_section, 'sezione')
        Toast.success(locale('Articolo eliminato'))
      },
      onError: (res) => {
        Toast.error(errorMessage, { content: res.message })
        setIsDeleting(false)
      },
    })
  }

  const saveArticleHandler = () => {
    setIsSaving(true)
    httpRequest({
      method: 'PUT',
      path: `help/article/${editedArticle.id}`,
      data: {
        realm: realm,
        parent_section: editedArticle.parent_section,
        title: editedArticle.title,
        content: editedArticle.content,
        vis_accountant: editedArticle.vis_accountant ? 1 : 0,
        vis_company: editedArticle.vis_company ? 1 : 0,
        is_draft: editedArticle.is_draft ? 1 : 0,
        related_articles: editedArticle.related_articles
          .map((a: RelatedArticle) => a.id)
          .join(','),
      },
      onSuccess: (res: {
        data: {
          sidebar: SidebarType
          content: string
          updated_at: string
          updated_by: string
        }
      }) => {
        setEditedArticle({
          ...editedArticle,
          updated_at: res.data.updated_at,
          updated_by: res.data.updated_by,
        })
        setIsSaving(false)
        props.updateSidebar(res.data.sidebar)
        Toast.success(locale('Articolo salvato'))
      },
      onError: (res: ResponseInterface) => {
        setIsSaving(false)
        Toast.error(errorMessage, { content: res.message })
      },
    })
  }

  const saveArticleDetailsHandler = (
    visCompany: boolean,
    visAccountant: boolean,
    visDraft: boolean,
    title: string
  ) => {
    setIsSaving(true)
    httpRequest({
      method: 'PUT',
      path: `help/article/${editedArticle.id}/details`,
      data: {
        realm: realm,
        title: title,
        vis_accountant: visAccountant ? 1 : 0,
        vis_company: visCompany ? 1 : 0,
        is_draft: visDraft ? 1 : 0,
      },
      onSuccess: (res: {
        data: { updated_at: string; updated_by: string }
      }) => {
        setEditedArticle({
          ...editedArticle,
          title: title,
          vis_company: visCompany,
          vis_accountant: visAccountant,
          is_draft: visDraft,
          updated_at: res.data.updated_at,
          updated_by: res.data.updated_by,
        })
        setIsSaving(false)
        Toast.success(locale('Dettagli articolo salvati'))
      },
      onError: (res: ResponseInterface) => {
        setIsSaving(false)
        Toast.error(errorMessage, { content: res.message })
      },
    })
  }

  return (
    <>
      <ArticleLateralActions
        editedArticle={editedArticle}
        onSaveArticleDetails={saveArticleDetailsHandler}
        isDeleting={isDeleting}
        isSaving={isSaving}
        onDeleteArticle={deleteArticle}
        onSaveArticle={saveArticleHandler}
        isExternal={props.isExternal}
        statistics={props.statistics}
        subArticle={props.subArticle}
        viewportWidth={props.viewportWidth}
      />
      <ArticleContent
        hasSidebar={props.hasSidebar}
        fixedSideBar={props.fixedSidebar.fixedSideBar}
        embedded={props.embedded}
        editorActive={editorModeActive}
      >
        <ToolbarContainer id={'stickyToolbar'} />
        <AdminDataContainer>
          {editorModeActive ? (
              <FroalaEditor
                  value={editedArticle.content}
                  setValue={(data) => {
                    setEditedArticle({ ...editedArticle, content: data })
                  }}
              />
          ) : props.articlePreview()}
        </AdminDataContainer>
      </ArticleContent>
    </>
  )
}
export default ArticleAdminMode
